import { ChangeEvent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';

import './targets.scss';
import { Badge, CustomSwitch, CustomTable, Text } from '../../../../../shared/uiComponents';
import { AdminTypes } from '../../../../../components/Menu';
import { TargetBody, TargetHeader } from './tableInfo';
import CreateTarget from './Actions/CreateTarget';
import { targetStatusType } from './targetRow';
import { BadgeTypes } from '../../../../../shared/uiComponents/StatusBadge';

import { DispatchProperties, useSelector } from '../../../../../redux/store';
import { getTargets, setIncludeMastered } from '../../../../../redux/State/clientSlice/programTargetSlice';
import { TargetProperties } from '../../../../../redux/API/ClientAPIHelpers/programTargetsProperties';
import { TEXT } from '../../../../../shared/Helpers/colors';
import Delete from './Actions/deleteTarget';
import GraphsTarget from './Actions/TargetGraph/graphsTarget';
import Edit from './Actions/EditTarget';
import TargetDetailsModal from './Actions/targetDetailsModal';
import TargetMenu from './Actions/targetMenu';

const TargetMobileCard = ({ target }: { target: TargetProperties }) => {
	const [detailsOpen, setDetailsOpen] = useState<boolean>(false);

	const openDetails = () => setDetailsOpen(true);

	return (
		<>
			<div className="targetMobileCard">
				<div className="targetMobileCardHeader" onClick={openDetails}>
					<Text title={target.name} type="psm" fontWeight="medium" color={TEXT.PRIMARY} />
				</div>

				<div className="targetMobileCardBody" onClick={openDetails}>
					<div className="targetMobileCardRow">
						<Text title="Status:" type="pxs" color={TEXT.TERTIARY} />
						<Badge
							label={target.status.name.split(/(?=[A-Z])/).join(' ')}
							sizeType="sm"
							type={targetStatusType[target.status.name] as BadgeTypes}
						/>
					</div>
					<div className="targetMobileCardRow">
						<Text title="Response rate:" type="pxs" color={TEXT.TERTIARY} />
						<Text title={`${target.responsePercentage}%`} type="psm" fontWeight="medium" color={TEXT.SECONDARY} />
					</div>
					<div className="targetMobileCardRow">
						<Text title="Date opened:" type="pxs" color={TEXT.TERTIARY} />
						<Text
							title={target.dateOpened ? dayjs(target.dateOpened).format('MM/DD/YYYY') : '-'}
							type="psm"
							fontWeight="medium"
							color={TEXT.SECONDARY}
						/>
					</div>
					<div className="targetMobileCardRow">
						<Text title="Date Mastered:" type="pxs" color={TEXT.TERTIARY} />
						<Text
							title={target.dateMastered ? dayjs(target.dateMastered).format('MM/DD/YYYY') : '-'}
							type="psm"
							fontWeight="medium"
							color={TEXT.SECONDARY}
						/>
					</div>
					<div className="targetMobileCardRow">
						<Text title="Target type:" type="pxs" color={TEXT.TERTIARY} />
						<Text title={target.type.name} type="psm" fontWeight="medium" color={TEXT.SECONDARY} />
					</div>
				</div>

				<div className="targetMobileCardFooter">
					<Delete id={target.id} programId={target.programId} mobileButton />
					<div className="targetMobileCardFooterActions">
						<GraphsTarget
							targetId={target.id}
							targetName={target.name}
							type={target.type.id}
							isIcon={false}
							mobileButton
						/>
						<Edit item={target} programId={target.programId} mobileButton />
						<TargetMenu target={target} onDetailsClick={openDetails} />
					</div>
				</div>
			</div>

			<TargetDetailsModal target={target} open={detailsOpen} onClose={() => setDetailsOpen(false)} />
		</>
	);
};

const Targets = ({ programId }: { programId: string }) => {
	const dispatch = useDispatch<DispatchProperties>();

	const programTargets = useSelector((state) => state.programTarget.targets);
	const loading = useSelector((state) => state.programTarget.loadingGetTargets);
	const role = useSelector((state) => state.account.role);
	const includeMastered = useSelector((state) => state.programTarget.includeMastered);

	const isBT = role?.section.id === AdminTypes.bt;

	useEffect(() => {
		if (!programId) return;

		dispatch(getTargets({ programId, includeMastered }));
	}, [programId, dispatch, includeMastered]);

	const showMasteredTargetsHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		dispatch(setIncludeMastered(checked));
	};

	return (
		<div className="targetsContainer">
			{!isBT && (
				<div className="targetsDesktopActions">
					<CreateTarget programId={programId} />
					<CustomSwitch label="Show Mastered" checked={includeMastered} onChange={showMasteredTargetsHandler} />
				</div>
			)}
			<div className="targetsDesktop">
				<CustomTable
					data={{ query: programTargets }}
					headers={TargetHeader}
					TableBody={TargetBody}
					loading={loading}
					hasPagination={false}
					checkForLoading={false}
				/>
			</div>
			<div className="targetsMobile">
				{programTargets?.map((target) => (
					<TargetMobileCard key={target.id} target={target} />
				))}
				{!isBT && (
					<div className="targetsMobileActions">
						<CustomSwitch
							label="Show Mastered"
							checked={includeMastered}
							onChange={showMasteredTargetsHandler}
							backgroundType="base"
							fullWidth
						/>
						<CreateTarget programId={programId} mobileVersion />
					</div>
				)}
			</div>
		</div>
	);
};

export default Targets;
